import {
  CompositeFilterDescriptor,
  DataResult,
  FilterOperator,
  State,
} from '@progress/kendo-data-query';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { InputChangeEvent } from '@progress/kendo-react-inputs';
import { produce } from 'immer';
import _ from 'lodash';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useBreakpoints from '../../../../../../common/hooks/useBreakpoints';
import useMount from '../../../../../../common/hooks/useMount';
import { DataLoader } from '../../../../../../common/widgets/DataLoader';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import ExpandablePanel from '../../../../../../common/widgets/ExpandablePanel';
import Grid from '../../../../../../common/widgets/Grid';
import Input from '../../../../../../common/widgets/Input';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../../config';
import { EmployeeBenefitItemModel } from '../../../../models/EmployeeBenefitItemModel';
import { resources } from '../../../../resources/resources';
import { companyService } from '../../../../services/company';
import { employeeBenefitsService } from '../../../../services/employee-choices';
import { ListItem } from '../../types';
import {
  DateAddedCell,
  EmployeeCell,
  OrderIdCell,
  ProductsCell,
  StateCell,
  TotalCell,
  ViewDetailsCell,
} from '../Components/CustomCells';
import { SearchCrtIdToFieldName, SearchFieldNames } from '../helper';
import { OrderStatuses } from '../../../../enums';

export interface EmployeeBenefitsState {
  companies: ListItem[];
  benefits: {
    data: EmployeeBenefitItemModel[];
    dataState: State;
    total: number;
  };
}

const initialDataState: State = {
  skip: 0,
  take: config.grid.defaultPageSize,
  filter: {
    logic: 'and',
    filters: [
      {
        field: 'orderStatusId',
        operator: FilterOperator.NotEqualTo,
        value: OrderStatuses.Failed,
      },
    ],
  },
  sort: [{ field: 'dateAdded', dir: 'desc' }],
};

export default function EmployeeBenefitsPage() {
  const { isMobile } = useBreakpoints();
  const { t: translate } = useTranslation();
  const searchCrtItems: ListItem[] = [
    {
      id: 0,
      name: translate(resources.admin.employees.filters.searchDefault),
    },
    {
      id: 1,
      name: translate(resources.admin.employeeBenefits.grid.orderId),
    },
    {
      id: 2,
      name: translate(resources.admin.employeeBenefits.grid.employeeName),
    },
    {
      id: 3,
      name: translate(resources.admin.employees.details.general.cnp),
    },
    {
      id: 4,
      name: translate(resources.admin.employees.details.general.eid),
    },
    {
      id: 5,
      name: translate(resources.admin.employeeBenefits.grid.state),
    },
  ];
  const companyDefaultItem = {
    id: 0,
    name: translate(resources.admin.employees.filters.companyDefault),
  };

  const [state, setState] = useState<EmployeeBenefitsState>({
    companies: [] as ListItem[],
    benefits: {
      data: [] as EmployeeBenefitItemModel[],
      dataState: initialDataState,
    },
  } as EmployeeBenefitsState);

  const [companyFilter, setCompanyFilter] =
    useState<ListItem>(companyDefaultItem);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [searchCrtFilter, setSearchCrtFilter] = useState<ListItem>(
    searchCrtItems[0],
  );

  const getFilter = useCallback(
    (
      companyFilter: ListItem,
      searchCrtFilter: ListItem,
      searchFilter: string,
    ): CompositeFilterDescriptor => {
      const filter: CompositeFilterDescriptor = { logic: 'and', filters: [] };
      if (companyFilter.id !== 0) {
        filter.filters.push({
          field: 'companyId',
          operator: FilterOperator.EqualTo,
          value: companyFilter.id,
        });
      }
      if (!_.isEmpty(searchFilter)) {
        if (searchCrtFilter.id === 0) {
          const filters = SearchFieldNames.map((s: string) => {
            return {
              field: s,
              operator: FilterOperator.Contains,
              value: searchFilter.toLowerCase(),
              ignoreCase: true,
            };
          });

          filter.filters.push({
            logic: 'or',
            filters,
          } as CompositeFilterDescriptor);
        } else {
          filter.filters.push({
            field: SearchCrtIdToFieldName[searchCrtFilter.id],
            operator: FilterOperator.Contains,
            value: searchFilter.toLowerCase(),
            ignoreCase: true,
          });
        }
      }
      //add default filter
      filter.filters.push({
        field: 'orderStatusId',
        operator: FilterOperator.NotEqualTo,
        value: OrderStatuses.Failed,
      });
      return filter;
    },
    [],
  );

  const onCampanyFilterChanged = (e: DropDownListChangeEvent) => {
    const company = e.value as ListItem;
    setCompanyFilter(company);
    setState(
      produce(d => {
        d.benefits.dataState.skip = 0;
        d.benefits.dataState.filter = getFilter(
          company,
          searchCrtFilter,
          searchFilter,
        );
      }),
    );
  };

  const onSearchCrtFilterChanged = (e: DropDownListChangeEvent) => {
    const searchCrt = e.value as ListItem;
    setSearchCrtFilter(searchCrt);
    setState(
      produce(d => {
        d.benefits.dataState.filter = getFilter(
          companyFilter,
          searchCrt,
          searchFilter,
        );
      }),
    );
  };

  const onSearchFilterChange = (e: InputChangeEvent) => {
    const search = e.value;
    setSearchFilter(search);
    setState(
      produce(d => {
        d.benefits.dataState.skip = 0;
        d.benefits.dataState.filter = getFilter(
          companyFilter,
          searchCrtFilter,
          search,
        );
      }),
    );
  };

  const onDataReceived = (result: DataResult) => {
    setState(
      produce(draft => {
        draft.benefits.data = result.data;
        draft.benefits.total = result.total;
      }),
    );
  };

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setState(
      produce(draft => {
        draft.benefits.dataState = e.dataState;
      }),
    );
  };

  const loadOtherData = async (
    abortController: AbortController | undefined,
  ) => {
    const result = await companyService.getCompaniesByClient(
      '?$select=companyId, name',
      {
        signal: abortController?.signal,
      },
    );
    if (!result.hasErrors) {
      setState(
        produce(d => {
          d.companies = [
            companyDefaultItem,
            ...(result.data.map(c => {
              return { id: c.companyId, name: c.name };
            }) as ListItem[]),
          ];
        }),
      );
    }
  };

  useMount(async abortController => {
    await loadOtherData(abortController);
  });

  return (
    <div className="panel padding-xl benefits-list-section">
      <h1>{translate(resources.admin.employeeBenefits.title)}</h1>

      {/* Employees search*/}
      {!isMobile ? (
        <div className="employee-search benefits-search">
          <div>
            <div>
              <div className="employee-search-column esi-c">
                <Dropdown
                  value={companyFilter}
                  defaultValue={companyDefaultItem}
                  data={_.orderBy(state.companies, 'id', 'asc')}
                  dataItemKey="id"
                  textField="name"
                  onChange={onCampanyFilterChanged}
                />
              </div>
              <div className="employee-search-column esi-e">
                <Dropdown
                  value={searchCrtFilter}
                  defaultValue={searchCrtItems[0]}
                  data={searchCrtItems}
                  dataItemKey="id"
                  textField="name"
                  onChange={onSearchCrtFilterChanged}
                />
                <Input
                  type="text"
                  value={searchFilter}
                  placeholder={translate(
                    resources.admin.employees.filters.searchPlaceholder,
                  )}
                  onChange={onSearchFilterChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="employee-search-mobile benefits-search">
          <div className="esi-e">
            <Dropdown
              value={searchCrtFilter}
              defaultValue={searchCrtItems[0]}
              data={searchCrtItems}
              dataItemKey="id"
              textField="name"
              onChange={onSearchCrtFilterChanged}
            />
            <Input
              type="text"
              value={searchFilter}
              placeholder={translate(
                resources.admin.employees.filters.searchPlaceholder,
              )}
              onChange={onSearchFilterChange}
            />
          </div>
          <ExpandablePanel
            expandOn={'header'}
            arrow={false}
            title={<div>{translate(resources.admin.advancedSearchLabel)}</div>}
          >
            <div className="admin-employees-adv-search">
              <div className="esi-c">
                <Dropdown
                  value={companyFilter}
                  defaultValue={companyDefaultItem}
                  data={_.orderBy(state.companies, 'id', 'asc')}
                  dataItemKey="id"
                  textField="name"
                  onChange={onCampanyFilterChanged}
                />
              </div>
            </div>
          </ExpandablePanel>
        </div>
      )}
      <DataLoader
        getData={employeeBenefitsService.getEmployeesBenefits}
        dataState={state.benefits.dataState}
        onDataReceived={onDataReceived}
      />
      <div className="admin-grid choices-grid">
        <Loader loading={!state.benefits.data} loader={<LoadingSpinner />}>
          <Grid
            data={state.benefits.data}
            pageable={{
              pageSizes: config.grid.pageSizes,
              pageSizeValue: state.benefits.dataState.take,
              info: false,
              previousNext: true,
            }}
            {...state.benefits.dataState}
            onDataStateChange={e => onDataStateChange(e)}
            total={state.benefits.total}
            sortable={{ allowUnsort: true, mode: 'multiple' }}
            sort={state.benefits.dataState.sort}
            columns={[
              {
                title: translate(resources.admin.employeeBenefits.grid.orderId),
                field: 'orderId',
                cell: OrderIdCell,
                sortable: false,
              },
              {
                title: translate(
                  resources.admin.employeeBenefits.grid.dateAdded,
                ),
                field: 'dateAdded',
                cell: DateAddedCell,
                sortable: true,
              },
              {
                title: translate(
                  resources.admin.employeeBenefits.grid.employeeName,
                ),
                field: 'employee',
                cell: EmployeeCell,
                sortable: true,
              },
              {
                title: translate(resources.admin.employeeBenefits.grid.state),
                field: 'state',
                cell: StateCell,
                sortable: true,
              },
              {
                title: translate(
                  resources.admin.employeeBenefits.grid.products,
                ),
                field: 'productNames',
                cell: ProductsCell,
                sortable: false,
              },
              {
                title: translate(resources.admin.employeeBenefits.grid.total),
                field: 'total',
                cell: TotalCell,
                sortable: true,
                headerClassName: 'grid-number',
              },
              { headerClassName: 'grid-actions-cell', cell: ViewDetailsCell },
            ]}
          />
        </Loader>
      </div>
    </div>
  );
}
